import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authApi } from '@/api/auth'
import type { User } from '@/types/api'
import { queryClient } from '@/main'

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  hasCheckedAuth: boolean
  isHydrated: boolean

  // Actions
  setUser: (user: User) => void
  setTokens: (accessToken: string, refreshToken: string) => void
  login: (user: User, accessToken: string, refreshToken: string) => void
  logout: () => void
  checkAuth: () => Promise<void>
  setHydrated: () => void

  // RBAC helpers
  hasPermission: (permission: string) => boolean
  isSuper: () => boolean
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      hasCheckedAuth: false,
      isHydrated: false,

      setUser: (user) => set({ user }),

      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),

      login: (user, accessToken, refreshToken) =>
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          hasCheckedAuth: true,
          isHydrated: true,
        }),

      logout: () => {
        queryClient.clear()
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        })
      },

      checkAuth: async () => {
        const { accessToken } = get()

        if (!accessToken) {
          set({
            isAuthenticated: false,
            isLoading: false,
            hasCheckedAuth: true,
          })
          return
        }

        set({ isLoading: true })

        try {
          const response = await authApi.me()
          const user = response.data.data

          set({
            user: user!,
            isAuthenticated: true,
            isLoading: false,
            hasCheckedAuth: true,
          })
        } catch (error: any) {
          // 토큰 만료 시 갱신 시도
          if (error.response?.status === 401) {
            const { refreshToken } = get()
            if (refreshToken) {
              try {
                const refreshResponse = await authApi.refresh(refreshToken)
                const data = refreshResponse.data.data!
                const { user, accessToken: newAccessToken, refreshToken: newRefreshToken } = data

                set({
                  user,
                  accessToken: newAccessToken,
                  refreshToken: newRefreshToken,
                  isAuthenticated: true,
                  isLoading: false,
                  hasCheckedAuth: true,
                })
                return
              } catch {
                // 갱신 실패 시 로그아웃 진행
              }
            }
          }

          // 실패 시 인증 정보 초기화
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            hasCheckedAuth: true,
          })
        }
      },

      setHydrated: () => {
        set({ isHydrated: true })
      },

      // RBAC helpers
      hasPermission: (permission) => {
        const user = get().user
        if (!user) return false
        if (user.isAdminSuper) return true
        return user.adminPermissions?.includes(permission) ?? false
      },
      isSuper: () => get().user?.isAdminSuper ?? false,
    }),
    {
      name: 'admin-auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        // isAuthenticated는 저장하지 않음 (항상 checkAuth()로 확인)
      }),
      onRehydrateStorage: () => {
        return (state, error) => {
          if (error) {
            console.error('Failed to rehydrate auth state:', error)
            if (state) {
              state.user = null
              state.accessToken = null
              state.refreshToken = null
              state.isAuthenticated = false
              state.hasCheckedAuth = false
              state.isHydrated = true
            }
            return
          }

          if (state) {
            state.isAuthenticated = false
            state.hasCheckedAuth = false
            state.isHydrated = true
          }
        }
      },
    }
  )
)
